/**
 * Purge Filters Component
 *
 * Filter panel for FI Data Purge page with:
 * - Date range selector
 * - Sort by dropdown
 * - Quick filter buttons
 * - Aggregation level tabs (Day/Month)
 */

import React, { useCallback, useMemo } from 'react';
import { Button } from 'equal-ds-ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { PurgeFiltersState, PurgeSortOrder, PurgeQuickFilter } from '../../../types/purge';
import {
  calculateQuickFilterDates,
} from '../../../utils/purge-utils';

const MONTHS = [
  { value: 0, label: 'January' },
  { value: 1, label: 'February' },
  { value: 2, label: 'March' },
  { value: 3, label: 'April' },
  { value: 4, label: 'May' },
  { value: 5, label: 'June' },
  { value: 6, label: 'July' },
  { value: 7, label: 'August' },
  { value: 8, label: 'September' },
  { value: 9, label: 'October' },
  { value: 10, label: 'November' },
  { value: 11, label: 'December' },
];

export interface PurgeFiltersProps {
  filters: PurgeFiltersState;
  onFiltersChange: (changes: Partial<PurgeFiltersState>) => void;
  onApplyFilters: () => void;
  hasUnappliedChanges: boolean;
}

const SORT_OPTIONS: { value: PurgeSortOrder; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'highest', label: 'Highest Count' },
  { value: 'lowest', label: 'Lowest Count' },
];

const QUICK_FILTERS: { value: PurgeQuickFilter; label: string }[] = [
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
];

export const PurgeFilters: React.FC<PurgeFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  hasUnappliedChanges,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Generate year options (last 3 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear];
  }, []);

  // Ensure dates are Date objects (defensive parsing)
  const ensureDate = (date: Date | string | null | undefined): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    return new Date(date);
  };

  // Get current start/end month and year from filters with defensive parsing
  const startDate = useMemo(() => ensureDate(filters.dateRange.startDate), [filters.dateRange.startDate]);
  const endDate = useMemo(() => ensureDate(filters.dateRange.endDate), [filters.dateRange.endDate]);

  const startMonth = startDate.getMonth();
  const startYear = startDate.getFullYear();
  const endMonth = endDate.getMonth();
  const endYear = endDate.getFullYear();

  const handleStartMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMonth = parseInt(e.target.value, 10);
      const newStartDate = new Date(startYear, newMonth, 1);
      onFiltersChange({
        dateRange: { startDate: newStartDate, endDate: endDate },
        quickFilter: 'custom',
      });
    },
    [startYear, endDate, onFiltersChange]
  );

  const handleStartYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newYear = parseInt(e.target.value, 10);
      const newStartDate = new Date(newYear, startMonth, 1);
      onFiltersChange({
        dateRange: { startDate: newStartDate, endDate: endDate },
        quickFilter: 'custom',
      });
    },
    [startMonth, endDate, onFiltersChange]
  );

  const handleEndMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMonth = parseInt(e.target.value, 10);
      // Set to last day of the month
      const newEndDate = new Date(endYear, newMonth + 1, 0);
      onFiltersChange({
        dateRange: { startDate: startDate, endDate: newEndDate },
        quickFilter: 'custom',
      });
    },
    [endYear, startDate, onFiltersChange]
  );

  const handleEndYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newYear = parseInt(e.target.value, 10);
      // Set to last day of the month
      const newEndDate = new Date(newYear, endMonth + 1, 0);
      onFiltersChange({
        dateRange: { startDate: startDate, endDate: newEndDate },
        quickFilter: 'custom',
      });
    },
    [endMonth, startDate, onFiltersChange]
  );

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFiltersChange({ sortBy: e.target.value as PurgeSortOrder });
    },
    [onFiltersChange]
  );

  const handleQuickFilterClick = useCallback(
    (filter: PurgeQuickFilter) => {
      if (filter === 'custom') {
        onFiltersChange({ quickFilter: filter });
      } else {
        const { startDate, endDate } = calculateQuickFilterDates(filter);
        onFiltersChange({
          quickFilter: filter,
          dateRange: { startDate, endDate },
        });
      }
    },
    [onFiltersChange]
  );

  // Temporarily disabled - aggregation toggle hidden
  // const handleAggregationChange = useCallback(
  //   (tabId: string) => {
  //     onFiltersChange({ aggregationLevel: tabId as 'day' | 'month' });
  //   },
  //   [onFiltersChange]
  // );

  return (
    <div className="bg-white rounded-lg border border-border-subtle">
      {/* Filter Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary uppercase tracking-wide">
            Filter By
          </span>
          {hasUnappliedChanges && (
            <span className="text-xs bg-accent-yellow text-text-primary px-2 py-1 rounded">
              Unapplied Changes
            </span>
          )}
        </div>
        <button
          className="text-text-secondary hover:text-text-primary transition-colors"
          aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Start Month */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                From:
              </label>
              <div className="flex gap-2">
                <select
                  value={startMonth}
                  onChange={handleStartMonthChange}
                  className="flex-1 h-10 px-3 py-2 border border-border-subtle rounded-md bg-white text-text-primary text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none bg-no-repeat bg-right pr-8"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <select
                  value={startYear}
                  onChange={handleStartYearChange}
                  className="w-24 h-10 px-3 py-2 border border-border-subtle rounded-md bg-white text-text-primary text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none bg-no-repeat bg-right pr-8"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* End Month */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                To:
              </label>
              <div className="flex gap-2">
                <select
                  value={endMonth}
                  onChange={handleEndMonthChange}
                  className="flex-1 h-10 px-3 py-2 border border-border-subtle rounded-md bg-white text-text-primary text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none bg-no-repeat bg-right pr-8"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <select
                  value={endYear}
                  onChange={handleEndYearChange}
                  className="w-24 h-10 px-3 py-2 border border-border-subtle rounded-md bg-white text-text-primary text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none bg-no-repeat bg-right pr-8"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Sort By:
              </label>
              <select
                value={filters.sortBy}
                onChange={handleSortChange}
                className="w-full h-10 px-3 py-2 border border-border-subtle rounded-md bg-white text-text-primary text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue appearance-none bg-no-repeat bg-right pr-10"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                }}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Aggregation Level - Temporarily hidden */}
            {/* <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                View:
              </label>
              <TabSwitcher
                tabs={[
                  { id: 'month', label: 'Monthly' },
                  { id: 'day', label: 'Daily' },
                ]}
                activeTab={filters.aggregationLevel}
                onTabChange={handleAggregationChange}
              />
            </div> */}
          </div>

          {/* Quick Filters */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Quick Filters:
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {QUICK_FILTERS.map((filter) => (
                <Button
                  key={filter.value}
                  variant={filters.quickFilter === filter.value ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handleQuickFilterClick(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
              {/* Apply Button - at the extreme right for visibility */}
              {hasUnappliedChanges && (
                <>
                  <div className="flex-grow" />
                  <Button variant="primary" size="sm" onClick={onApplyFilters}>
                    Apply Filters
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
